import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetAccountQuery, useGetPlansQuery } from 'store/api';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import StorageIndicator from 'components/storage-indicator/StorageIndicator';
import prologo from 'assets/images/pro-logo.png';
import prologo_big from 'assets/images/pro-logo-big.png';
import infinity_logo from 'assets/images/icon-infite.png';

import { classNames } from 'utils/helpers';
import { formatPrice } from 'utils/formatPrice';

const ProItems = () => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col gap-2'>
  
      <CheckItem variant='pro'>{t('account.plans.pro_item_2')}</CheckItem>
      <CheckItem variant='pro'>{t('account.plans.pro_item_3')}</CheckItem>
      <CheckItem variant='pro'>
        {t('account.plans.pro_item_4')}
        <span className='text-brand-gold'>{t('account.plans.pro_item_4_strong')}</span>
      </CheckItem>
    </div>
  );
};

const FreeItems = () => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col gap-2'>
      <CheckItem variant='free'>{t('account.plans.free_item_1')}</CheckItem>
      <CheckItem variant='free'>{t('account.plans.free_item_2')}</CheckItem>
    </div>
  );
};

const Price = ({ price }) => {
  const { t } = useTranslation();

  return (
    <div className='mt-3 flex gap-2 !font-thunder items-baseline'>
      <span className='font-thunder text-2xl uppercase'>ARS</span>
      <p className='!text-[35px]'>${price}</p>
      <span className='!text-base'>{t('account.plans.per_month')}</span>
    </div>
  );
};

const FreemiumCard = ({ title, usedSpace, totalSpace, variant = 'pro', children, subscription = false, price = 0 }) => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-col gap-6 xl:w-1/2 w-full'>
      <div className='flex justify-between w-full h-[64px]'>
        <div className='flex flex-col gap-2'>
          <div>
            <h4
              className={classNames({
                'text-[22px] !font-archivo !font-semibold !normal-case xl:': true,
                'text-brand-gold': variant === 'pro',
              })}
            >
              {title}
            </h4>
          </div>
          <Price price={price} />
        </div>
        <div>{variant === 'pro' && !subscription && <img src={prologo} className='h-[72px] w-[72px]' />}</div>
      </div>
      <div className='flex flex-col items-start h-16 gap-2'>
        <h5 className='text-neutral-silver-200 !text-base !font-archivo'>{t('account.plans.storage')}</h5>
        {variant === 'pro' ? (
          <div className='flex gap-2 items-center mt-[-8px]'>
            <img src={infinity_logo} className='w-12 h-12' />
            <p className='text-brand-gold text-left !text-xs'>
              {t('account.plans.storage_msg_1')}
              <br />
              {t('account.plans.storage_msg_2')}
            </p>
          </div>
        ) : (
          <StorageIndicator
            usedSpace={usedSpace}
            totalSpace={totalSpace}
            reverse
            size='full'
            upgrade={false}
            showInMobile
            hasSubscription={false}
          />
        )}
      </div>
      {variant === 'pro' ? <ProItems /> : <FreeItems />}
      {variant === 'pro' && !subscription && (
        <Link
          to='/profile/account/subscription/plan'
          className='bg-brand-gold w-full text-lg font-bold px-5 py-2 rounded-xl text-neutral-black text-center'
        >
          {t('account.plans.button')}
        </Link>
      )}
    </div>
  );
};

const CheckItem = ({ variant = 'pro', children }) => {
  const color = variant === 'pro' ? 'text-brand-gold' : 'text-brand-uva';
  return (
    <div className='flex items-center flex-row gap-1 text-neutral-50 !text-sm text-left'>
      <CheckCircleIcon className={color + ' h-6 w-6 mr-1'} />
      {children}
    </div>
  );
};

const AccountPlan = () => {
  const [price, setPrice] = useState(0);
  const { data: account = {}, isLoading } = useGetAccountQuery();
  const { data: plan = {} } = useGetPlansQuery();

  useEffect(() => {
    if (isLoading) return;

    if (account?.has_subscription) {
      let price = account?.subscription?.plan?.pricing_data?.price || 0;
      price = price / (account?.subscription?.plan?.billing_frequency || 1);
      setPrice(formatPrice(price || '0'));
    } else if (plan?.plans?.length > 0) {
      const monthlyPlan = plan.plans.find((p) => p.billing_frequency === 1) || plan.plans[0];
      if (monthlyPlan) {
        setPrice(formatPrice(monthlyPlan.pricing_data.price || '0'));
      }
    }
  }, [account, plan, isLoading]);

  return (
    <>
      <div className={`container-accountPlan ${account.has_subscription && 'pro-container'}`}>
        <div className='flex gap-y-8 md:gap-x-6 flex-wrap xl:flex-nowrap flex-col w-full xl:flex-row '>
          {!account?.has_subscription && (
            <FreemiumCard
              title={'Chest Free'}
              variant='free'
              usedSpace={account?.used_seconds}
              totalSpace={account?.total_seconds}
            />
          )}
          <FreemiumCard title='Chest Pro' subscription={account?.has_subscription} price={price} />
          {account.has_subscription && (
            <div className='xl:w-2/4 w-full'>
              <img src={prologo_big} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountPlan;
